/**
 * The kitchen: three appliances you open to see inside.
 *
 * Replaced a screen that was doing three jobs at once — telling you what was
 * expiring, offering seven category tiles to add from, and listing everything —
 * which is why it read as a form rather than a place. Adding moved to the dock;
 * this screen only shows you what you have.
 *
 * Opening one closes the others. Two open fridges is not a thing, and it keeps
 * the list below unambiguous about what it's listing.
 */

import type { ReactNode } from "react";

import { searchItems } from "../features/food/searchItems";
import { ExpiryBand } from "./ExpiryBand";
import { Icon } from "./Icon";
import { SearchResults } from "./SearchResults";
import { InventoryList } from "./InventoryList";
import { Appliance } from "./Appliance";
import { STORAGE_LOCATIONS } from "../features/food/types";
import type { FoodCategory, FoodItem, StorageLocation } from "../features/food/types";

import "./KitchenScreen.css";

export type KitchenView = "have" | "buy";

interface KitchenScreenProps {
  items: FoodItem[];
  today: string;
  /** Which appliance is open, or null for none. Owned by the shell. */
  open: StorageLocation | null;
  onOpen: (place: StorageLocation | null) => void;
  onOpenItem: (item: FoodItem) => void;
  /** What's typed in the search box, owned by the shell so a tab away keeps it. */
  query: string;
  onQuery: (query: string) => void;
  /** Which half of the kitchen you're looking at. */
  view: KitchenView;
  onView: (view: KitchenView) => void;
  /** How many things are waiting to be bought, for the tab's count. */
  toBuy: number;
  /** The shopping list, rendered by the shell so this screen stays layout. */
  cart: ReactNode;
}

/** The distinct categories in a place, most-stocked first. */
function categoriesIn(items: FoodItem[], place: StorageLocation): FoodCategory[] {
  const counts = new Map<FoodCategory, number>();
  for (const item of items) {
    if (item.location !== place) continue;
    counts.set(item.category, (counts.get(item.category) ?? 0) + 1);
  }
  return [...counts.entries()]
    .sort((a, b) => b[1] - a[1])
    .map(([category]) => category);
}

export function KitchenScreen({
  items,
  today,
  open,
  onOpen,
  onOpenItem,
  query,
  onQuery,
  view,
  onView,
  toBuy,
  cart,
}: KitchenScreenProps) {
  const inPlace = (place: StorageLocation) =>
    items.filter((item) => item.location === place);

  return (
    <>
      {/*
        Two states food has relative to you, on one screen.

        Not a fifth tab: the dock is already ~130px of permanent chrome and a
        fifth target makes every one of them narrower. And "what I have" and
        "what I need" are the same question asked twice, which is an argument
        for one screen rather than two.
      */}
      <div className="kitchen__views" role="tablist" aria-label="Kitchen">
        <button
          className="kitchen__view"
          type="button"
          role="tab"
          aria-selected={view === "have"}
          onClick={() => onView("have")}
        >
          In the house
        </button>
        <button
          className="kitchen__view"
          type="button"
          role="tab"
          aria-selected={view === "buy"}
          onClick={() => onView("buy")}
        >
          To buy
          {toBuy > 0 && <span className="kitchen__count">{toBuy}</span>}
        </button>
      </div>

      {view === "buy" ? (
        cart
      ) : (
        <>
      {/*
        Search, above the doors.

        Three closed appliances is the right default — it makes this screen a
        place rather than a list. It is also exactly wrong when you already know
        what you're after and only want to know whether you have it, which is
        the question people actually open a food app to ask. So the doors stay
        until something is typed, and step aside while it is.
      */}
      <div className="kitchen__search">
        <Icon name="search" className="kitchen__search-icon" />
        <input
          className="kitchen__search-input"
          type="search"
          value={query}
          placeholder="Search everything"
          aria-label="Search everything in the kitchen"
          onChange={(event) => onQuery(event.target.value)}
        />
        {query.length > 0 && (
          <button
            className="kitchen__search-clear"
            type="button"
            aria-label="Clear search"
            onClick={() => onQuery("")}
          >
            <Icon name="close" className="kitchen__search-clear-icon" />
          </button>
        )}
      </div>

      {query.trim().length > 0 ? (
        <SearchResults
          results={searchItems(items, query, today)}
          query={query}
          today={today}
          onOpen={onOpenItem}
        />
      ) : (
        <>
      <ExpiryBand items={items} today={today} />

      <div className="kitchen">
        {STORAGE_LOCATIONS.map((place) => (
          <Appliance
            key={place}
            place={place}
            count={inPlace(place).length}
            contents={categoriesIn(items, place)}
            open={open === place}
            // Tapping the open one closes it, so there's always a way back to
            // the whole kitchen without hunting for a close button.
            onToggle={(next) => onOpen(open === next ? null : next)}
          />
        ))}
        {/* The floor the three of them stand on. Without it they float. */}
        <div className="kitchen__floor" aria-hidden="true" />
      </div>

      {open === null ? (
        <p className="kitchen__hint">
          {items.length === 0
            ? "Nothing in here yet. Use the camera or + below to put something in."
            : "Open one to see what's inside."}
        </p>
      ) : (
        <div className="kitchen__contents">
          {inPlace(open).length === 0 ? (
            <p className="kitchen__hint">Empty.</p>
          ) : (
            <InventoryList
              items={items}
              locations={[open]}
              today={today}
              onOpen={onOpenItem}
            />
          )}
        </div>
      )}
        </>
      )}
        </>
      )}
    </>
  );
}
